import axios, { type AxiosError, type AxiosProgressEvent } from 'axios';
import { 
  ActionHistory, 
  ApprovalContext,
  ProjectSchedule, 
  TaskStartResponse, 
  TaskStatus, 
  ProjectResetResponse,
  DocumentAssociation,
  FileListResponse,
  CreateFilesResponse
} from '../types';
import { setupAxiosInterceptors } from '../utils/axiosSetup';
import { looksLikeUuidFileStem, resolveDownloadFileName } from '../utils/downloadFileName';

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000',
  timeout: 30000,
  withCredentials: true, // CRITICAL: Send session cookies with all requests
});

/** Parse filename from Content-Disposition (filename= / filename*=). */
export function parseContentDispositionFilename(
  header: string | undefined | null
): string | null {
  if (!header || typeof header !== 'string') return null;
  const star = /filename\*=(?:UTF-8''|utf-8'')([^;]+)/i.exec(header);
  if (star) {
    try {
      return decodeURIComponent(star[1].trim().replace(/^["']|["']$/g, ''));
    } catch {
      /* fall through */
    }
  }
  const quoted = /filename="([^"]+)"/i.exec(header);
  if (quoted) return quoted[1];
  const plain = /filename=([^;\s]+)/i.exec(header);
  if (plain) return plain[1].replace(/^["']|["']$/g, '');
  return null;
}

function sanitizeDownloadFileName(name: string): string {
  const base = (name || 'download').trim() || 'download';
  return base.replace(/[<>:"/\\|?*\x00-\x1f]/g, '_').slice(0, 200);
}

// Set up standard interceptors (includes 401 handling)
setupAxiosInterceptors(apiClient);

/** Sniff common raster signatures when Content-Type is missing (e.g. CORS header visibility). */
async function blobLooksLikeRasterImage(blob: Blob): Promise<boolean> {
  const t = (blob.type || '').toLowerCase();
  if (t.startsWith('image/')) return true;
  if (blob.size < 12) return false;
  const head = new Uint8Array(await blob.slice(0, 16).arrayBuffer());
  if (head[0] === 0xff && head[1] === 0xd8 && head[2] === 0xff) return true;
  if (head[0] === 0x89 && head[1] === 0x50 && head[2] === 0x4e && head[3] === 0x47) return true;
  if (head[0] === 0x47 && head[1] === 0x49 && head[2] === 0x46) return true;
  if (head[0] === 0x52 && head[1] === 0x49 && head[2] === 0x46 && head[3] === 0x46) {
    const tag = String.fromCharCode(head[8], head[9], head[10], head[11]);
    return tag === 'WEBP';
  }
  return false;
}

/** Random SharePoint folder image for authenticated app background; returns null on failure or non-image response. */
export async function getSharePointBackgroundImageBlob(): Promise<Blob | null> {
  try {
    const response = await apiClient.get('/api/sp/background-image', {
      responseType: 'blob',
      validateStatus: (status) => status === 200,
    });
    if (response.status !== 200 || !(response.data instanceof Blob)) {
      if (import.meta.env.DEV) {
        console.warn('[background-image] Bad status or not a blob', response.status);
      }
      return null;
    }
    const blob = response.data;
    const headerRaw = response.headers['content-type'] ?? response.headers['Content-Type'];
    const headerCt = String(headerRaw || '').toLowerCase();
    const blobType = (blob.type || '').toLowerCase();
    const looksImage =
      headerCt.startsWith('image/') ||
      blobType.startsWith('image/') ||
      (await blobLooksLikeRasterImage(blob));
    if (!looksImage) {
      if (import.meta.env.DEV) {
        let preview = '';
        try {
          preview = (await blob.slice(0, 200).text()).trim().slice(0, 120);
        } catch {
          /* ignore */
        }
        console.warn('[background-image] Rejected as non-image', {
          headerCt: headerRaw,
          blobType: blob.type,
          size: blob.size,
          preview,
        });
      }
      return null;
    }
    return blob;
  } catch (err) {
    const ax = err as AxiosError<Blob>;
    const status = ax.response?.status;
    const data = ax.response?.data;
    if (status === 404 && data instanceof Blob) {
      try {
        const text = await data.text();
        const parsed = JSON.parse(text) as {
          code?: string;
          message?: string;
          path?: string;
          hint?: string;
          folderName?: string;
          libraryName?: string;
        };
        console.warn('[background-image] Server 404 body:', parsed);
        if (parsed.code === 'NOT_FOUND') {
          console.warn(
            '[background-image] API route is not registered on this backend (got generic NOT_FOUND). Copy latest BACKEND (especially src/routes/sp.js), restart Node on port 3001, and confirm GET /api/sp/background-image is in the deployed build.'
          );
        }
        if (parsed.code === 'BACKGROUND_FOLDER_NOT_FOUND' || parsed.code === 'NO_BACKGROUND_IMAGES') {
          console.warn('[background-image] SharePoint folder/library mismatch. Check SP_BACKGROUND_IMAGES_FOLDER, SP_BACKGROUND_LIBRARY_NAME, and server logs [SP background-image].');
        }
      } catch {
        console.warn('[background-image] Request failed', status, ax.message);
      }
    } else if (import.meta.env.DEV) {
      console.warn('[background-image] Request failed', status, ax.message);
    }
    return null;
  }
}

export interface HealthResponse {
  status: string;
  timestamp: string;
}

export interface CompletionDetails {
  actionId: number;
  completedBy: {
    userId: number;
    displayName: string;
    email: string;
  };
  completedAt: string; // ISO 8601
  details: {
    spItemId?: number;
    fileName?: string;
    uploadedAt?: string;
    [key: string]: unknown;
  };
}

export interface RevisionUploadResponse {
  success: boolean;
  message: string;
  uploaderEmail: string;
  uploadedAt: string; // ISO 8601
  fileName: string;
  originalFileName?: string;
  // New fields for Sprint 29: Multiple User/Action Support
  alreadyCompleted?: boolean;
  completion?: CompletionDetails;
}

export interface CreateFileResponse {
  success: boolean;
  message: string;
  spItemId: number;
  itemId: string;
  webUrl: string;
  fileName: string;
  metadata: {
    fileName: string;
    fileSize: number;
    mimeType: string;
    extension: string;
  };
  actionId: number;
  // New fields for Sprint 29: Multiple User/Action Support
  alreadyCompleted?: boolean;
  completion?: CompletionDetails;
}

export interface CreateFileError {
  error: boolean;
  message: string;
  code?: 'VALIDATION_ERROR' | 'INVALID_EMAIL' | 'NO_PROJECTS' | 'FILE_NOT_FOUND' | 'ACCESS_DENIED' | 'GRAPH_API_ERROR' | 'LIBRARY_NOT_FOUND' | 'INVALID_AUTHENTICATION_TOKEN' | 'TOKEN_EXPIRED' | 'APP_TOKEN_EXPIRED' | 'AUTH_ERROR';
}

export interface SharePointItem {
  name: string;
  itemId: string;
  driveId: string;
  siteId: string;
  webUrl: string;
  size: number;
  lastModifiedDateTime: string;
  isFolder?: boolean;
  isFile?: boolean;
}

export interface HistoryResponse {
  actions: ActionHistory[];
}

export interface SharePointListResponse {
  items: SharePointItem[];
}

export interface LargeFileInfo {
  fileId: number;
  fileName: string;
  fileSize: number;
  mimeType: string | null;
  uploadedAt: string;
  storageType: 'LOCAL';
  actionDocumentId?: number | null;
}

export const apiService = {
  async checkHealth(): Promise<HealthResponse> {
    const response = await apiClient.get('/health');
    return response.data;
  },

  async listSharePointFiles(
    siteId: string,
    driveId?: string,
    libraryName?: string,
    folderPath?: string
  ): Promise<SharePointListResponse> {
    // Build query string manually to ensure proper encoding of folderPath
    // Use encodeURIComponent to encode spaces as %20 instead of + signs
    const params: string[] = [`siteId=${encodeURIComponent(siteId)}`];
    if (driveId) {
      params.push(`driveId=${encodeURIComponent(driveId)}`);
    }
    if (libraryName) {
      params.push(`libraryName=${encodeURIComponent(libraryName)}`);
    }
    if (folderPath) {
      // Explicitly encode folderPath to ensure %20 for spaces instead of +
      params.push(`folderPath=${encodeURIComponent(folderPath)}`);
    }
    const response = await apiClient.get(`/api/sp/list?${params.join('&')}`);
    return response.data;
  },

  // Project-scoped SharePoint file listing (Sprint 32)
  async listProjectSharePointFiles(
    projectId: number,
    path?: string
  ): Promise<SharePointListResponse> {
    // Build query string manually to ensure proper encoding of folderPath
    // Use encodeURIComponent to encode spaces as %20 instead of + signs
    let url = `/api/sp/list?projectId=${projectId}`;
    if (path && path.trim() !== '') {
      // Encode the folder path using encodeURIComponent to ensure %20 for spaces
      const encodedPath = encodeURIComponent(path.trim());
      url += `&folderPath=${encodedPath}`;
    }
    const response = await apiClient.get(url);
    return response.data;
  },

  async getSharePointItem(
    siteId: string,
    driveId: string,
    itemId: string
  ): Promise<SharePointItem> {
    const response = await apiClient.get('/api/sp/item', {
      params: { siteId, driveId, itemId },
    });
    return response.data;
  },

  // Project-scoped SharePoint item retrieval (Sprint 32)
  async getProjectSharePointItem(
    projectId: number,
    itemId: string
  ): Promise<SharePointItem> {
    const response = await apiClient.get('/api/sp/item', {
      params: { projectId, itemId },
    });
    return response.data;
  },

  async downloadSharePointFile(
    siteId: string,
    driveId: string,
    itemId: string
  ): Promise<Blob> {
    const response = await apiClient.get('/api/sp/download', {
      params: { siteId, driveId, itemId },
      responseType: 'blob',
    });
    return response.data;
  },

  // Project-scoped SharePoint file download (Sprint 32)
  async downloadProjectSharePointFile(
    projectId: number,
    itemId: string
  ): Promise<Blob> {
    const response = await apiClient.get('/api/sp/download', {
      params: { projectId, itemId },
      responseType: 'blob',
    });
    return response.data;
  },

  // Project-scoped SharePoint folder creation
  // Creates a new folder within the project's SharePoint structure
  // Path is relative to the project root (e.g., 'SubFolder1/NewFolder')
  async createProjectFolder(
    projectId: number,
    folderPath: string
  ): Promise<SharePointItem> {
    const response = await apiClient.post('/api/sp/folder', {
      projectId,
      folderPath: folderPath.trim(),
    });
    return response.data;
  },

  async getActionHistory(
    projectId: number,
    spItemId?: number
  ): Promise<HistoryResponse> {
    const params: { projectId: number; spItemId?: number } = { projectId };
    if (spItemId) {
      params.spItemId = spItemId;
    }
    const response = await apiClient.get('/api/history', { params });
    return response.data;
  },

  async sendReview(
    siteId: string,
    driveId: string,
    itemId: string,
    recipientEmail: string,
    userAccessToken?: string | null
  ): Promise<void> {
    const response = await apiClient.post('/api/review', {
      siteId,
      driveId,
      itemId,
      recipientEmail,
      userAccessToken: userAccessToken || null, // Include token for delegated auth
    });
    return response.data;
  },

  async sendApproval(
    siteId: string,
    driveId: string,
    itemId: string,
    recipientEmail: string,
    userAccessToken?: string | null
  ): Promise<void> {
    const response = await apiClient.post('/api/approval', {
      siteId,
      driveId,
      itemId,
      recipientEmail,
      userAccessToken: userAccessToken || null, // Include token for delegated auth
    });
    return response.data;
  },

  async getApprovalContext(params: { token?: string; actionId?: number }): Promise<ApprovalContext> {
    const { token, actionId } = params;
    if (!token && (actionId == null || actionId === 0)) {
      throw new Error('getApprovalContext requires token or actionId');
    }
    const query = token
      ? `?token=${encodeURIComponent(token)}`
      : `?actionId=${actionId}`;
    const response = await apiClient.get(`/api/approval/context${query}`, {
      withCredentials: true,
    });
    return response.data;
  },

  async getRevisionContext(actionId: number): Promise<ApprovalContext> {
    const response = await apiClient.get(`/api/revision/context?actionId=${actionId}`, {
      withCredentials: true,
    });
    return response.data;
  },

  async approveAction(
    token: string | null,
    approverEmail: string,
    actionId?: number | null,
    comment?: string | null
  ): Promise<void> {
    // Use actionId-based authentication if token is not provided
    let url = token
      ? `/api/approval/approve?token=${encodeURIComponent(token)}`
      : actionId
        ? `/api/approval/approve?actionId=${actionId}`
        : '/api/approval/approve';
    if (token && actionId) {
      url += `&actionId=${encodeURIComponent(String(actionId))}`;
    }

    const requestData: { approverEmail: string; token?: string; actionId?: number; comment?: string } = {
      approverEmail,
      ...(token ? { token } : {}),
      ...(actionId != null && actionId > 0 ? { actionId } : {})
    };
    if (comment != null && comment.trim() !== '') {
      requestData.comment = comment.trim();
    }
    
    // Sprint G2: 120s timeout so Airport Manager approval (e.g. 1.1.3) does not time out; backend sends response after completeAction.
    const response = await apiClient.post(url, requestData, {
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true, // Ensure session cookies are sent
      timeout: 120000,
    });
    return response.data;
  },

  async rejectAction(
    token: string | null,
    rejecterEmail: string,
    actionId?: number | null,
    comment?: string | null
  ): Promise<{ projectId?: number }> {
    let url = token
      ? `/api/approval/reject?token=${encodeURIComponent(token)}`
      : actionId
        ? `/api/approval/reject?actionId=${actionId}`
        : '/api/approval/reject';
    if (token && actionId) {
      url += `&actionId=${encodeURIComponent(String(actionId))}`;
    }

    const requestData: { rejecterEmail: string; token?: string; actionId?: number; comment?: string } = {
      rejecterEmail,
      ...(token ? { token } : {}),
      ...(actionId != null && actionId > 0 ? { actionId } : {})
    };
    if (comment != null && comment.trim() !== '') {
      requestData.comment = comment.trim();
    }
    
    const response = await apiClient.post(url, requestData, {
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true, // Ensure session cookies are sent
    });
    return response.data;
  },

  async sendRevision(
    siteId: string,
    driveId: string,
    itemId: string,
    recipientEmail: string,
    userAccessToken?: string | null
  ): Promise<void> {
    const response = await apiClient.post('/api/revision', {
      siteId,
      driveId,
      itemId,
      recipientEmail,
      userAccessToken: userAccessToken || null, // Include token for delegated auth
    });
    return response.data;
  },

  async uploadRevision(
    token: string | null,
    uploaderEmail: string,
    file: File,
    actionId?: number | null
  ): Promise<RevisionUploadResponse> {
    const formData = new FormData();
    
    // Backend validation strategy:
    // - If token is provided, send it (email-based access)
    // - If actionId is provided without token, don't send token field at all
    //   Backend should check for actionId first and skip token validation
    // - If neither is provided, this will fail (handled below)
    if (token) {
      formData.append('token', token);
    }
    // When actionId is provided without token, we don't send token field
    // Backend should recognize actionId in query/FormData and use session authentication
    
    // uploaderEmail is always required
    if (!uploaderEmail || uploaderEmail.trim() === '') {
      throw new Error('uploaderEmail is required');
    }
    formData.append('uploaderEmail', uploaderEmail);
    formData.append('file', file);
    
    // Include actionId if provided - this ensures the action is marked complete and cascading updates are triggered
    // Backend will use actionId directly if provided, otherwise falls back to token lookup
    if (actionId !== null && actionId !== undefined) {
      const actionIdString = actionId.toString();
      formData.append('actionId', actionIdString);
    }
    
    if (!token && !actionId) {
      console.warn('[Revision Upload] Frontend: No actionId and no token provided. Backend may reject this request.');
    }
    
    // Build URL with query parameters (similar to review API pattern)
    // Backend may check query params before FormData validation
    let url = '/api/revision/upload';
    if (actionId !== null && actionId !== undefined) {
      url += `?actionId=${actionId}`;
    } else if (token) {
      url += `?token=${encodeURIComponent(token)}`;
    }
    
    try {
      const response = await apiClient.post(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      // Enhanced error logging for revision upload failures
      if (error.response?.status === 400) {
        console.error('[Revision Upload] Backend validation error:', {
          status: error.response.status,
          errorMessage: error.response.data?.message || error.response.data?.error || 'Unknown error',
          errorData: error.response.data,
          requestDetails: {
            url,
            hasToken: !!token,
            hasActionId: actionId !== null && actionId !== undefined,
            uploaderEmail,
          },
        });
      }
      throw error;
    }
  },

  // Workflow API endpoints
  async getProjectSchedule(projectId: number): Promise<ProjectSchedule> {
    const response = await apiClient.get('/api/workflow/schedule', {
      params: { projectId }
    });
    return response.data;
  },

  async startTask(
    taskId: number,
    projectId: number,
    phaseId: number,
    fileData: { siteId: string; driveId: string; itemId: string },
    emails: { revisionEmail?: string; approvalEmail?: string; reviewEmail?: string },
    userAccessToken?: string | null
  ): Promise<TaskStartResponse> {
    const payload: Record<string, unknown> = {
      taskId,
      projectId,
      phaseId,
      ...fileData,
      ...emails
    };
    
    // Include userAccessToken if provided
    if (userAccessToken) {
      payload.userAccessToken = userAccessToken;
    }
    
    const response = await apiClient.post('/api/workflow/task/start', payload);
    return response.data;
  },

  async getTaskStatus(taskInstanceId: number): Promise<TaskStatus> {
    const response = await apiClient.get(`/api/workflow/task/${taskInstanceId}/status`);
    return response.data;
  },

  async resetProject(projectId: number): Promise<ProjectResetResponse> {
    const response = await apiClient.post('/api/workflow/reset', {
      projectId
    });
    return response.data;
  },

  // Create Action - File Upload to SharePoint
  // libraryName is optional - backend auto-detects from airport's default library if not provided
  async createFile(
    file: File,
    projectId: number,
    siteId: string,
    folderPath: string,
    recipientEmail?: string,
    userAccessToken?: string | null,
    onUploadProgress?: (progressEvent: { loaded: number; total: number }) => void,
    actionId?: number | null,
    libraryName?: string | null // Optional - backend auto-detects from airport if not provided (moved to end for easier omission)
  ): Promise<CreateFileResponse> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('projectId', projectId.toString());
    formData.append('siteId', siteId);
    // libraryName removed - backend auto-detects from airport's default library
    // Only append if explicitly provided (for backward compatibility)
    if (libraryName) {
      formData.append('libraryName', libraryName);
    }
    formData.append('folderPath', folderPath);
    
    if (recipientEmail) {
      formData.append('recipientEmail', recipientEmail);
    }
    
    if (userAccessToken) {
      formData.append('userAccessToken', userAccessToken);
    }
    
    // Include actionId if provided - this ensures the action is marked complete and cascading updates are triggered
    if (actionId !== null && actionId !== undefined) {
      const actionIdString = actionId.toString();
      formData.append('actionId', actionIdString);
    }

    const config: { headers: Record<string, string>; onUploadProgress?: (progressEvent: AxiosProgressEvent) => void } = {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    };

    if (onUploadProgress) {
      config.onUploadProgress = (progressEvent: AxiosProgressEvent) => {
        const total = progressEvent.total ?? 0;
        if (total > 0) {
          onUploadProgress({ loaded: progressEvent.loaded, total });
        }
      };
    }

    // Auth: apiClient sends session cookie + Authorization Bearer (via interceptors). Backend requireAuth sets req.user so completion can write Execution KPI audit (ACTION_ACTUAL_END_SET).
    const response = await apiClient.post('/api/create', formData, config);
    return response.data;
  },

  // SharePoint Comments API (Sprint G3: optional versionLabel for old document versions)
  async getDocumentComments(spItemId: number, versionLabel?: string | null): Promise<{
    spItemId: number;
    versionLabel?: string | null;
    comments: Array<{
      actionId: number;
      actionType: string;
      comments: string;
      commentedAt: string;
      commentedBy: {
        userId: number;
        email: string;
        displayName: string;
      } | null;
    }>;
  }> {
    const params: { spItemId: number; versionLabel?: string } = { spItemId };
    if (versionLabel != null && versionLabel.trim() !== '') params.versionLabel = versionLabel.trim();
    const response = await apiClient.get('/api/sp/comments', { params });
    return response.data;
  },

  async getDocumentCommentsBySharePointId(
    siteId: string,
    driveId: string,
    itemId: string,
    versionLabel?: string | null
  ): Promise<{
    spItemId: number | null;
    siteId: string;
    driveId: string;
    itemId: string;
    versionLabel?: string | null;
    comments: Array<{
      actionId: number;
      actionType: string;
      comments: string;
      commentedAt: string;
      commentedBy: {
        userId: number;
        email: string;
        displayName: string;
      } | null;
    }>;
  }> {
    const params: { siteId: string; driveId: string; itemId: string; versionLabel?: string } = {
      siteId,
      driveId,
      itemId,
    };
    if (versionLabel != null && versionLabel.trim() !== '') params.versionLabel = versionLabel.trim();
    const response = await apiClient.get('/api/sp/comments', { params });
    return response.data;
  },

  /** Sprint G3: Get version labels that have comments for a document (for version selector). */
  async getDocumentCommentVersionLabels(spItemId: number): Promise<{ spItemId: number; versionLabels: string[] }> {
    const response = await apiClient.get('/api/sp/comments/version-labels', {
      params: { spItemId },
    });
    return response.data;
  },

  // ============================================================================
  // Multi-Document and Large File API Methods
  // ============================================================================

  // Document Associations
  async getActionDocuments(actionId: number): Promise<DocumentAssociation[]> {
    const response = await apiClient.get(`/api/actions/${actionId}/documents`);
    
    // Backend returns either { documents: [...] } or { actionDocuments: [...] } or array directly
    let result = response.data.documents || response.data.actionDocuments || response.data;
    
    // Ensure result is an array
    if (!Array.isArray(result)) {
      console.error(`[API] getActionDocuments(${actionId}) - Result is not an array!`, {
        result,
        resultType: typeof result,
      });
      result = [];
    }
    
    return result;
  },

  async createActionDocuments(
    actionId: number,
    associations: Array<{
      documentType?: string;
      documentNumber?: number;
      documentTitle?: string; // NEW - Document title
      documentDescription?: string; // NEW - Document description
      sourceActionId?: number; // NEW - Source CREATE action ID (for REVISE/REVIEW/APPROVE)
      sourceActionDocumentId?: number; // NEW - Specific document ID from source action
      isRequired: boolean;
    }>
  ): Promise<DocumentAssociation[]> {
    const response = await apiClient.post(`/api/actions/${actionId}/documents`, {
      documentAssociations: associations,
    });
    
    return response.data.actionDocuments || response.data;
  },

  async updateActionDocuments(
    actionId: number,
    associations: Array<{
      actionDocumentId: number;
      documentTitle?: string | null; // Document title (can be null to clear)
      documentDescription?: string | null; // Document description (can be null to clear)
      targetFolderPath?: string | null; // SharePoint folder path where document should be saved
      documentType?: string;
      documentNumber?: number;
      sourceActionId?: number | null; // Source CREATE action ID (for REVISE/REVIEW/APPROVE)
      sourceActionDocumentId?: number | null; // Specific document ID from source action (for REVISE/REVIEW/APPROVE)
      isRequired?: boolean;
    }>
  ): Promise<DocumentAssociation[]> {
    try {
      const response = await apiClient.put(`/api/actions/${actionId}/documents`, {
        documentAssociations: associations,
      });
      
      // Backend returns either { actionDocuments: [...] } or { documents: [...] } or array directly
      const result = response.data?.actionDocuments || response.data?.documents || response.data || [];
      
      return Array.isArray(result) ? result : [];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      // Handle specific error codes from backend for validation
      if (error.response?.data?.code === 'TITLE_REQUIRED') {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const apiError: any = new Error(error.response.data.message || 'Document title is required for multi-document CREATE actions');
        apiError.code = 'TITLE_REQUIRED';
        throw apiError;
      } else if (error.response?.data?.code === 'TITLE_TOO_LONG') {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const apiError: any = new Error(error.response.data.message || `Document title must not exceed 100 characters`);
        apiError.code = 'TITLE_TOO_LONG';
        throw apiError;
      } else if (error.response?.data?.code === 'DESCRIPTION_TOO_LONG') {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const apiError: any = new Error(error.response.data.message || `Document description must not exceed 500 characters`);
        apiError.code = 'DESCRIPTION_TOO_LONG';
        throw apiError;
      }
      throw error;
    }
  },

  async deleteActionDocument(actionId: number, documentId: number): Promise<void> {
    await apiClient.delete(`/api/actions/${actionId}/documents/${documentId}`);
  },

  // Multi-File Upload
  async createFiles(
    formData: FormData,
    options?: {
      onUploadProgress?: (progressEvent: { loaded: number; total: number }) => void;
    }
  ): Promise<CreateFilesResponse> {
    const config: any = {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    };

    if (options?.onUploadProgress) {
      config.onUploadProgress = (progressEvent: any) => {
        if (progressEvent.total) {
          options.onUploadProgress!({
            loaded: progressEvent.loaded,
            total: progressEvent.total,
          });
        }
      };
    }

    // Auth: same as createFile – apiClient sends session + JWT; backend requireAuth required for completion/audit.
    const response = await apiClient.post('/api/create', formData, config);
    return response.data;
  },

  // File Browser
  async getSharePointFiles(params: {
    projectId?: number;
    page?: number;
    pageSize?: number;
  }): Promise<FileListResponse> {
    const queryParams = new URLSearchParams();
    if (params.projectId) queryParams.append('projectId', params.projectId.toString());
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.pageSize) queryParams.append('pageSize', params.pageSize.toString());

    const response = await apiClient.get(`/api/files/sharepoint?${queryParams.toString()}`);
    return response.data;
  },

  async getLocalFiles(params: {
    projectId?: number;
    page?: number;
    pageSize?: number;
  }): Promise<FileListResponse> {
    const queryParams = new URLSearchParams();
    if (params.projectId) queryParams.append('projectId', params.projectId.toString());
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.pageSize) queryParams.append('pageSize', params.pageSize.toString());

    const response = await apiClient.get(`/api/files/local?${queryParams.toString()}`);
    return response.data;
  },

  async downloadFileWithInfo(
    fileId: number,
    storageType: 'SHAREPOINT' | 'LOCAL'
  ): Promise<{ blob: Blob; fileName: string | null }> {
    const response = await apiClient.get(`/api/files/${fileId}/download`, {
      params: { storageType },
      responseType: 'blob',
    });
    const cd = response.headers['content-disposition'];
    const fileName = parseContentDispositionFilename(
      typeof cd === 'string' ? cd : undefined
    );
    return { blob: response.data, fileName };
  },

  async downloadFile(fileId: number, storageType: 'SHAREPOINT' | 'LOCAL'): Promise<Blob> {
    const { blob } = await this.downloadFileWithInfo(fileId, storageType);
    return blob;
  },

  async viewFileWithInfo(
    fileId: number,
    storageType: 'SHAREPOINT' | 'LOCAL'
  ): Promise<{ blob: Blob; fileName: string | null }> {
    const response = await apiClient.get(`/api/files/${fileId}/view`, {
      params: { storageType },
      responseType: 'blob',
    });
    const cd = response.headers['content-disposition'];
    const fileName = parseContentDispositionFilename(
      typeof cd === 'string' ? cd : undefined
    );
    return { blob: response.data, fileName };
  },

  async viewFile(fileId: number, storageType: 'SHAREPOINT' | 'LOCAL'): Promise<Blob> {
    const { blob } = await this.viewFileWithInfo(fileId, storageType);
    return blob;
  },

  /** Save a blob with a stable file name (avoids anonymous blob-URL names in Save As). */
  triggerBlobDownload(blob: Blob, preferredFileName: string): void {
    let safe = sanitizeDownloadFileName(preferredFileName);
    if (looksLikeUuidFileStem(safe)) {
      safe = sanitizeDownloadFileName(
        resolveDownloadFileName({ headerFileName: null, blob })
      );
    }
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = safe;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  },

  /** List large files (network drive) by folder context. Virtual hierarchy. */
  async getLargeFiles(params: {
    projectId: number;
    phaseId?: number;
    taskId?: number;
    actionId?: number;
  }): Promise<{ success: boolean; folders: { id: number; name: string; type: string }[]; files: LargeFileInfo[] }> {
    const queryParams = new URLSearchParams();
    queryParams.append('projectId', params.projectId.toString());
    if (params.phaseId != null) queryParams.append('phaseId', params.phaseId.toString());
    if (params.taskId != null) queryParams.append('taskId', params.taskId.toString());
    if (params.actionId != null) queryParams.append('actionId', params.actionId.toString());
    const response = await apiClient.get(`/api/files/large?${queryParams.toString()}`);
    return response.data;
  },

  /** Download by ActionDocumentId (document-centric). */
  async downloadDocument(documentId: number): Promise<Blob> {
    const response = await apiClient.get(`/api/documents/${documentId}/download`, {
      responseType: 'blob',
    });
    return response.data;
  },

  /** View document inline by ActionDocumentId. */
  async viewDocument(documentId: number): Promise<Blob> {
    const response = await apiClient.get(`/api/documents/${documentId}/view`, {
      responseType: 'blob',
    });
    return response.data;
  },

  // Action Management (Enhanced)
  async getAction(actionId: number): Promise<{
    actionId: number;
    taskId: number;
    actionType: string;
    requiredDocumentCount?: number | null;
    documents?: DocumentAssociation[];
    [key: string]: unknown;
  }> {
    const response = await apiClient.get(`/api/actions/${actionId}`);
    return response.data;
  },

  async updateAction(actionId: number, updates: {
    requiredDocumentCount?: number;
    [key: string]: unknown;
  }): Promise<any> {
    const response = await apiClient.put(`/api/actions/${actionId}`, updates);
    return response.data;
  },

  /** System Admin only. Resends assignment email for an IN_PROGRESS action (no status/date changes). */
  async resendActionAssignmentEmail(
    actionId: number,
    userAccessToken?: string | null
  ): Promise<{ success: boolean; actionId: number; actionType: string; recipientsNotified: number }> {
    const response = await apiClient.post(`/api/actions/${actionId}/resend-email`, {
      userAccessToken: userAccessToken ?? null,
    });
    return response.data;
  },
};


